<template>
  <div>
    <h1 class="page-header">{{ $t('reportBasicSalaryHistory.pageTitle') }}</h1>
    <div class="ts-flex ts--mx-4">
      <div class="ts-w-3/12 ts-px-4">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="exporting">
              <div class="row">
                <div class="col-sm-12">
                  <div class="form-group m-b-10">
                    <label class="col-form-label control-label">{{
                      $t('reportBasicSalaryHistory.orgChartStructure')
                    }}</label>
                    <div>
                      <ts-loading-banner :loading="loadingPreData">
                        <ts-search-select
                          v-model="org_chart_structure_id"
                          :options="
                            orgChartStructures.map(o => ({
                              value: o.org_chart_structure_id,
                              text: o.org_chart_structure
                            }))
                          "
                        />
                      </ts-loading-banner>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-12">
                  <div class="form-group m-b-10">
                    <label class="col-form-label control-label">
                      {{ $t('reportBasicSalaryHistory.cycleYear') }}
                    </label>
                    <div>
                      <ts-year-picker-input v-model="cycle_year" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-sm-12 col-lg-12 col-md-12 ts-text-right">
                  <b-dropdown
                    dropup
                    :disabled="viewing || org_chart_structure_id === ''"
                    :text="$t('button.export')"
                    variant="outline-default"
                    class="mr-2"
                    :waiting="exporting"
                  >
                    <b-dropdown-item href="#" @click="exportExcel">
                      <i class="fas fa-file-excel ts-text-green-500"></i>
                      {{ $t('button.excel') }}
                    </b-dropdown-item>
                    <b-dropdown-item href="#" @click="exportPdf">
                      <i class="fas fa-file-pdf ts-text-red-500"></i>
                      {{ $t('button.pdf') }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <ts-button
                    color="primary"
                    @click="viewReport"
                    :waiting="viewing"
                    :disabled="viewing || org_chart_structure_id === ''"
                  >
                    {{ $t('button.view') }}
                  </ts-button>
                </div>
              </div>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="ts-w-9/12 ts-px-4">
        <ts-card>
          <div class="viewer overflow-auto">
            <ts-table :columns="columns" :records="resources" :loading="loading">
              <template v-slot="{ record }">
                <td class="ts-whitespace-no-wrap">{{ record.card_id }}</td>
                <td class="ts-whitespace-no-wrap">
                  {{ record.employee_name_en + (record.employee_name_kh ? '|' + record.employee_name_kh : '') }}
                </td>
                <td class="ts-whitespace-no-wrap">{{ record.job_title }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Jan }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Feb }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Mar }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Apr }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.May }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Jun }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Jul }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Aug }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Sep }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Oct }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Nov }}</td>
                <td class="ts-whitespace-no-wrap text-center">{{ record.Dec }}</td>
              </template>
            </ts-table>
          </div>
          <div class="d-flex justify-content-between align-items-center p-15">
            <ts-total-list v-model="pagination" />
            <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
          </div>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { trim } from 'lodash'
import { mapState, mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import PreviewPdf from './preview-pdf'

export default {
  name: 'report-payroll-detail',
  components: {
    PreviewPdf
  },
  data() {
    return {
      org_chart_structure_id: '',
      cycle_year: moment().year(),
      loading: false,
      loadingPreData: false,
      exporting: false,
      viewing: false,
      errors: new Errors()
    }
  },
  computed: {
    ...mapState('hr/reportBasicSalaryHistory', ['resources', 'pagination', 'orgChartStructures']),
    search: {
      get() {
        return this.$store.state.hr.reportBasicSalaryHistory.search
      },
      set(newValue) {
        this.$store.commit('hr/reportBasicSalaryHistory/SET_SEARCH', newValue)
        this.$store.commit('hr/reportBasicSalaryHistory/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('reportBasicSalaryHistory.cardId')
        },
        {
          name: this.$t('reportBasicSalaryHistory.employeeName')
        },
        {
          name: this.$t('reportBasicSalaryHistory.jobTitle')
        },
        {
          name: this.$t('reportBasicSalaryHistory.jan'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.feb'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.mar'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.apr'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.may'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.jun'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.jul'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.agu'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.sep'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.oct'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.nov'),
          style: 'text-align: center;'
        },
        {
          name: this.$t('reportBasicSalaryHistory.dec'),
          style: 'text-align: center;'
        }
      ]
    }
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  methods: {
    ...mapActions('hr/reportBasicSalaryHistory', ['fetchOrgStructures']),
    fetchResourceBeforeCreate() {
      this.loadingPreData = true
      Promise.all([this.fetchOrgStructures()]).then(() => {
        this.loadingPreData = false
      })
    },
    fetch(attributes) {
      return this.$store.dispatch('hr/reportBasicSalaryHistory/fetch', {
        ...attributes,
        org_chart_structure_id: this.org_chart_structure_id,
        cycle_year: this.cycle_year
      })
    },

    viewReport(attributes) {
      this.viewing = true
      this.loading = true
      this.fetch(attributes)
        .then(() => {
          this.viewing = false
          this.loading = false
        })
        .catch(error => {
          this.viewing = false
          this.loading = false
          this.$toasted.error(error.message)
        })
    },

    fetchData(attributes) {
      this.loading = true
      this.fetch(attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },

    exportExcel() {
      this.exporting = true

      this.$store
        .dispatch('hr/reportBasicSalaryHistory/exportExcel', {
          org_chart_structure_id: this.org_chart_structure_id,
          cycle_year: this.cycle_year
        })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(response.headers['content-disposition'].indexOf('filename'))
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
          this.$toasted.error(error.message)
        })
        .finally(() => (this.exporting = false))
    },
    exportPdf() {
      this.$modal.show(
        PreviewPdf,
        {
          org_chart_structure_id: this.org_chart_structure_id,
          cycle_year: this.cycle_year
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('hr/reportBasicSalaryHistory/RESET_STATE')
    next()
  }
}
</script>
<style lang="scss" scoped>
table td {
  vertical-align: middle;
}
.viewer {
  min-height: 171px;
}
::-webkit-scrollbar {
  height: 8px;
  width: 3px; /* Remove scrollbar space */
  background: #ffff; /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #e5e7e7;
}
div::-webkit-scrollbar-thumb:hover {
  background: #b7b7b8;
}
</style>
